import { count } from '../core/words.js';
import { buildDiagram } from './roomDiagram.js';
import { formatSpan, formatTime } from '../sim/clock.js';
import { LINENS } from '../data/objects.js';

/**
 * The printed BEO, held up in front of your face — and, on the back of it, your
 * card.
 *
 * The board pauses the game and this does not, and that difference is the whole
 * point of it existing. Reading the diagram is the skill; the sheet is what
 * reading it costs. You slow to a walk, you cannot set anything down, and you
 * cannot push the cart — so checking whether it was three across or two is a
 * decision with a price, made in the middle of the room, against a deadline.
 *
 * It is deliberately not a minimap and deliberately not a checklist. It says
 * what the event wants. It never says where you are, and nothing anywhere in
 * the game marks the target anchors on the floor.
 *
 * The card is the other page, on the action key while the sheet is up, and it is here rather than in a panel of
 * its own for the reason everything else about escalation is where it is: the
 * player must always be able to find out exactly where they stand, and the
 * cheapest way to guarantee that is to put it on the thing already in their
 * hand. It costs what the sheet costs — you slow to a walk and your hands are
 * full — which is the right price for reading your own file in a corridor.
 *
 * Every line on it carries the day it was filed and the day it comes off. Those
 * dates are the whole of the mechanic: a strike you can count down is a bad
 * week, and a strike with no date on it is a permanent mark with a friendly
 * face.
 */
export function createSheet({ parent, grid }) {
  const root = document.createElement('div');
  root.className = 'sheet';
  root.hidden = true;
  parent.append(root);

  let shownId = null;

  return {
    root,
    get open() {
      return !root.hidden;
    },

    hide() {
      root.hidden = true;
      shownId = null;
    },

    /**
     * Draws the sheet for an accepted event.
     *
     * Rebuilt when the event changes or a shortfall line does, not every frame
     * — the countdown is the one live number and it is written on its own.
     */
    show(active, clock, shortfalls = []) {
      root.hidden = false;
      const beo = active.beo;

      /*
       * The shortfall lines are part of what the sheet *is*, not a live number
       * written over the top of it, so a change to one rebuilds the page. The
       * signature is cheap and the thing it guards is not: without it the sheet
       * would be rebuilt every frame you held it up.
       */
      const signature = `${beo.id}|${shortfalls
        .map((entry) => `${entry.id}:${entry.resolved}:${entry.lands}:${entry.waiting}`)
        .join('|')}`;
      if (shownId !== signature) {
        shownId = signature;
        const linen = beo.setup ? LINENS[beo.setup.linen] : null;
        /*
         * What the room is short of, who is holding it, and where it will be
         * when they let go.
         *
         * That last part is the only thing in the game that tells you what a
         * department makes of you, and it never says so: it says "the end cart"
         * or "the linen shelf, no fetch", and those are the same sentence about
         * Housekeeping told two ways. A player who reads this sheet twice in a
         * week and notices the second line has changed has learned the ladder
         * off the work, which is where it belongs — the alternative was a panel
         * with four words on it and nothing to do about any of them.
         *
         * Rebuilt whenever the line changes rather than only when the event
         * does, because standing moves during a shift and a sheet that is out
         * of date about where your linen is, is a sheet that sends you across
         * the property for nothing.
         */
        const short = shortfalls.length
          ? `<ul class="sheet-short">${shortfalls
              .map(
                (entry) =>
                  `<li data-solved="${entry.resolved ? 'yes' : 'no'}">
                    <span class="sheet-short-what">${entry.label}</span>
                    <span class="sheet-short-who">${entry.holderName}</span>
                    <span class="sheet-short-where" data-fetch="${entry.fetch ? 'yes' : 'no'}">${
                      entry.resolved ? entry.waiting ?? 'covered' : entry.lands
                    }</span>
                  </li>`
              )
              .join('')}</ul>`
          : '';

        /*
         * A strike has no diagram: it is a list of what is on the lawn, where
         * it goes, and by when, and the card carries those as lines.
         */
        if (!beo.setup) {
          root.innerHTML = `
          <div class="sheet-head">
            <span class="sheet-code">${beo.code}</span>
            <b>${beo.title}</b>
          </div>
          <dl class="sheet-spec">
            ${(beo.lines ?? []).map(([label, value]) => `<dt>${label}</dt><dd>${value}</dd>`).join('')}
            <dt>Due by</dt><dd>${formatTime(active.times.setupBy)}</dd>
          </dl>
          <p class="sheet-note">${beo.note ?? ''}</p>
          <div class="sheet-left"></div>`;
        } else {
        root.innerHTML = `
          <div class="sheet-head">
            <span class="sheet-code">${beo.code}</span>
            <b>${beo.title}</b>
          </div>
          <dl class="sheet-spec">
            <dt>Tables</dt><dd>${beo.setup.tableCount} × 72" round</dd>
            <dt>Chairs</dt><dd>${beo.setup.chairsPerTable} each</dd>
            <dt>Linen</dt><dd>${linen ? linen.name : beo.setup.linen}</dd>
            <dt>Set by</dt><dd>${formatTime(active.times.setupBy)}</dd>
          </dl>
          <div class="sheet-plan"></div>
          <ul class="sheet-staging">${(beo.setup.staging ?? [])
            .map((line) => `<li>${line}</li>`)
            .join('')}</ul>
          ${short}
          <div class="sheet-left"></div>`;
        root.querySelector('.sheet-plan').append(buildDiagram(beo.setup, beo.grid ?? grid, { compact: true }));
        }
      }

      const remaining = active.times.setupBy - clock.minutes;
      const left = root.querySelector('.sheet-left');
      const text =
        remaining >= 0 ? `${formatSpan(remaining)} left` : `OVERDUE ${formatSpan(-remaining)}`;
      if (left.textContent !== text) left.textContent = text;
      left.dataset.tone = remaining < 0 ? 'bad' : remaining < 60 ? 'warn' : 'idle';
    },

    /** What the sheet says when there is no event in your pocket. */
    showEmpty() {
      root.hidden = false;
      if (shownId !== '__empty__') {
        shownId = '__empty__';
        root.innerHTML = `<div class="sheet-head"><b>No sheet</b></div>
          <p class="sheet-none">Nothing accepted. The board is in the muster alcove.</p>
          <p class="sheet-none">[E] your card</p>`;
      }
    },

    /**
     * Your card: what is on it, what it was for, and when each line clears.
     *
     * `card` is `{ day, strikes, max, entries, served, next }` — the whole of
     * the escalation, in the order a person would want it: how close you are,
     * what each one was, when it comes off, and what happens if you get to the
     * end. Nothing on this page is a surprise and nothing on it is hidden,
     * which is the entire design rule the escalation was built to.
     */
    showCard(card) {
      root.hidden = false;
      const signature = `card|${card.day}|${card.strikes}|${card.served}|${card.entries
        .map((entry) => `${entry.reason}:${entry.clears}`)
        .join(',')}`;
      if (shownId === signature) return;
      shownId = signature;

      const rows = card.entries.length
        ? `<ul class="card-list">${card.entries
            .map(
              (entry) => `<li>
                <span class="card-what">${entry.what}</span>
                <span class="card-when">filed day ${entry.day}</span>
                <span class="card-clears">clears day ${entry.clears}${
                  entry.in > 0 ? ` · ${entry.in} ${entry.in === 1 ? 'shift' : 'shifts'}` : ''
                }</span>
              </li>`
            )
            .join('')}</ul>`
        : '<p class="sheet-none">Nothing on it. Keep it that way.</p>';

      /*
       * What the next one costs, said before it costs it. The terminal state is
       * on this page at all times rather than only once you are standing on it:
       * a floor you can only discover by hitting it is a floor nobody was
       * warned about.
       */
      const ahead = card.next.dismissed
        ? 'A third write-up and you are finished here. There is no fourth conversation.'
        : `A third write-up is ${count(card.next.shifts, 'shift')} off the roster.`;

      root.innerHTML = `
        <div class="sheet-head">
          <span class="sheet-code">CARD</span>
          <b>${card.strikes} of ${card.max}</b>
        </div>
        ${rows}
        <p class="card-ahead">${ahead}</p>
        ${
          card.served
            ? `<p class="card-served">${card.served} suspension${
                card.served === 1 ? '' : 's'
              } served. That does not come off.</p>`
            : ''
        }
        <div class="sheet-left">Day ${card.day}  ·  [E] back to the sheet</div>`;
    },
  };
}
